import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtNOK, fmtKwh, fmtDate, contractTypeLabel, invoiceStatusLabel, caseStatusLabel } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Mail, Phone, MapPin, Building2, User, FileText, Receipt, MessageSquare, Gauge, Sparkles, PowerOff, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { SupplierSwitchButton } from "@/components/SupplierSwitchButton";
import { NyAvtaleButton } from "@/components/NyAvtaleDialog";
import { PaymentAgreementsCard } from "@/components/PaymentAgreements";

export default function KundeDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/customers/${id}`);
        setData(data);
      } catch { setData({ error: true }); }
    })();
  }, [id]);

  if (!data) return <Spinner />;
  if (data.error) return <Empty title="Kunde ikke funnet" />;

  const { customer, metering_points, contracts, invoices, cases } = data;

  return (
    <div data-testid="kunde-detalj-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-400 hover:text-zinc-200 flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3.5 w-3.5" /> Tilbake til kundeliste
      </button>

      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-11 w-11 rounded-md bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-zinc-800 grid place-items-center">
              {customer.kind === "bedrift" ? <Building2 className="h-5 w-5 text-emerald-400" /> : <User className="h-5 w-5 text-emerald-400" />}
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">{customer.full_name}</h1>
              <div className="text-sm text-zinc-500 flex items-center gap-3 mt-0.5">
                <span className="font-mono text-emerald-400">{customer.customer_number}</span>
                <span>{customer.kind === "bedrift" ? `Orgnr ${customer.orgnr || "—"}` : `Privatkunde${customer.fnr_last4 ? ` · fnr *****${customer.fnr_last4}` : ""}`}</span>
                <StatusBadge value={customer.status} />
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {["INACTIVE", "CLOSED", "BLOCKED", "inaktiv", "sperret"].includes(customer.status) ? (
            <button onClick={async () => {
              try { await (await import("@/lib/api")).api.post(`/kraftadmin/customers/${customer.id}/restore`); toast.success("Kunde gjenopprettet"); window.location.reload(); }
              catch (e) { toast.error("Feilet"); }
            }} className="h-9 px-3 rounded-md bg-violet-500/10 border border-violet-500/30 text-violet-300 text-sm flex items-center gap-2 hover:bg-violet-500/20" data-testid="restore-btn">
              <RotateCw className="h-4 w-4" /> Gjenopprett kunde
            </button>
          ) : (
            <button onClick={async () => {
              if (!window.confirm("Er du sikker på at du vil deaktivere kunden? Historikk beholdes.")) return;
              try { await (await import("@/lib/api")).api.post(`/kraftadmin/customers/${customer.id}/deactivate`); toast.success("Kunde deaktivert"); window.location.reload(); }
              catch (e) { toast.error("Feilet"); }
            }} className="h-9 px-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2 hover:bg-red-500/20" data-testid="deactivate-btn">
              <PowerOff className="h-4 w-4" /> Deaktiver
            </button>
          )}
        </div>
      </div>

      {/* Kontakt-panel */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Kontakt</div>
          <div className="text-sm space-y-1.5">
            {customer.email && <div className="flex items-center gap-1.5 text-zinc-200"><Mail className="h-3.5 w-3.5 text-zinc-500" /> {customer.email}</div>}
            {customer.phone && <div className="flex items-center gap-1.5 text-zinc-200"><Phone className="h-3.5 w-3.5 text-zinc-500" /> {customer.phone}</div>}
            {customer.address && <div className="flex items-center gap-1.5 text-zinc-200"><MapPin className="h-3.5 w-3.5 text-zinc-500" /> {customer.address}, {customer.postal_code} {customer.city}</div>}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Målepunkter</div>
          <div className="font-heading text-2xl font-medium">{metering_points.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Avtaler</div>
          <div className="font-heading text-2xl font-medium">{contracts.length}</div>
          <div className="text-[11px] text-zinc-500 mt-1">{contracts.filter(c => c.status === "aktiv").length} aktive</div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Utestående</div>
          <div className="font-heading text-2xl font-medium text-amber-400">
            {fmtNOK(invoices.filter(i => ["sendt", "delvis_betalt", "purret"].includes(i.status)).reduce((s, i) => s + (i.total_nok - (i.paid_nok || 0)), 0))}
          </div>
        </Card>
      </div>

      <Tabs defaultValue="oversikt">
        <TabsList className="bg-zinc-950 border border-zinc-900 h-10">
          <TabsTrigger value="oversikt" data-testid="tab-oversikt">Oversikt</TabsTrigger>
          <TabsTrigger value="malepunkter" data-testid="tab-malepunkter">Målepunkter ({metering_points.length})</TabsTrigger>
          <TabsTrigger value="avtaler" data-testid="tab-avtaler">Avtaler ({contracts.length})</TabsTrigger>
          <TabsTrigger value="fakturaer" data-testid="tab-fakturaer">Fakturaer ({invoices.length})</TabsTrigger>
          <TabsTrigger value="saker" data-testid="tab-saker">Saker ({cases.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="oversikt" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PaymentAgreementsCard customer={customer} />
            <Card className="p-4">
              <div className="text-sm font-medium text-zinc-100 mb-3">Siste 3 fakturaer</div>
              {invoices.length === 0 ? <div className="text-xs text-zinc-500">Ingen fakturaer</div> : (
                <div className="space-y-2">
                  {invoices.slice(0, 3).map((i) => (
                    <Link to={`/fakturering/${i.id}`} key={i.id} className="flex items-center justify-between text-sm hover:bg-zinc-900/50 rounded px-2 py-1.5">
                      <div>
                        <div className="text-zinc-100 font-mono text-xs">{i.invoice_number}</div>
                        <div className="text-[11px] text-zinc-500">{fmtDate(i.issue_date)}</div>
                      </div>
                      <div className="text-right">
                        <div className="tabular-nums">{fmtNOK(i.total_nok)}</div>
                        <StatusBadge value={i.status} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-4">
              <div className="text-sm font-medium text-zinc-100 mb-3">Aktive avtaler</div>
              {contracts.filter(c => c.status === "aktiv").length === 0 ? (
                <div className="text-xs text-zinc-500">Ingen aktive avtaler</div>
              ) : (
                <div className="space-y-2">
                  {contracts.filter(c => c.status === "aktiv").slice(0, 3).map((c) => (
                    <div key={c.id} className="text-sm">
                      <div className="flex justify-between">
                        <span className="font-mono text-xs text-emerald-400">{c.contract_number}</span>
                        <StatusBadge value={c.status} />
                      </div>
                      <div className="text-zinc-300">{contractTypeLabel(c.type)}</div>
                      <div className="text-[11px] text-zinc-500">Fra {fmtDate(c.start_date)} · påslag {c.markup_ore_per_kwh} øre/kWh · fastbeløp {fmtNOK(c.monthly_fee_nok)}/mnd</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="malepunkter" className="mt-4">
          <Card className="overflow-hidden">
            {metering_points.length === 0 ? <Empty title="Ingen målepunkter" /> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60">
                    <th className="py-2 px-4 font-medium">MPID</th>
                    <th className="py-2 px-4 font-medium">Nettselskap</th>
                    <th className="py-2 px-4 font-medium">Adresse</th>
                    <th className="py-2 px-4 font-medium">Prisområde</th>
                    <th className="py-2 px-4 font-medium">Årsforbruk</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                    <th className="py-2 px-4 font-medium">Elhub</th>
                  </tr>
                </thead>
                <tbody>
                  {metering_points.map((mp) => (
                    <tr key={mp.id} className="border-t border-zinc-900" data-testid={`mp-row-${mp.mpid}`}>
                      <td className="py-2 px-4 font-mono text-xs text-emerald-400">{mp.mpid}</td>
                      <td className="py-2 px-4 text-zinc-300">{mp.grid_company}</td>
                      <td className="py-2 px-4 text-zinc-400 text-xs">{mp.address}, {mp.postal_code} {mp.city}</td>
                      <td className="py-2 px-4"><span className="text-xs bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">{mp.price_area}</span></td>
                      <td className="py-2 px-4 tabular-nums text-zinc-300">{fmtKwh(mp.annual_consumption_kwh)}</td>
                      <td className="py-2 px-4"><StatusBadge value={mp.status} /></td>
                      <td className="py-2 px-4"><SupplierSwitchButton mp={mp} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="avtaler" className="mt-4">
          <div className="flex justify-end mb-3"><NyAvtaleButton customerId={customer.id} onCreated={() => window.location.reload()} /></div>
          <Card className="overflow-hidden">
            {contracts.length === 0 ? <Empty title="Ingen avtaler" /> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60">
                    <th className="py-2 px-4 font-medium">Avtalenr</th>
                    <th className="py-2 px-4 font-medium">Type</th>
                    <th className="py-2 px-4 font-medium">Fra</th>
                    <th className="py-2 px-4 font-medium">Påslag</th>
                    <th className="py-2 px-4 font-medium">Fastbeløp</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id} className="border-t border-zinc-900" data-testid={`ctr-row-${c.contract_number}`}>
                      <td className="py-2 px-4 font-mono text-xs text-emerald-400">{c.contract_number}</td>
                      <td className="py-2 px-4 text-zinc-100">{contractTypeLabel(c.type)}</td>
                      <td className="py-2 px-4 text-zinc-400">{fmtDate(c.start_date)}</td>
                      <td className="py-2 px-4 tabular-nums text-zinc-300">{c.markup_ore_per_kwh?.toFixed(2)} øre/kWh</td>
                      <td className="py-2 px-4 tabular-nums text-zinc-300">{fmtNOK(c.monthly_fee_nok)}/mnd</td>
                      <td className="py-2 px-4"><StatusBadge value={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="fakturaer" className="mt-4">
          <Card className="overflow-hidden">
            {invoices.length === 0 ? <Empty title="Ingen fakturaer" /> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60">
                    <th className="py-2 px-4 font-medium">Fakturanr</th>
                    <th className="py-2 px-4 font-medium">Dato</th>
                    <th className="py-2 px-4 font-medium">Forfall</th>
                    <th className="py-2 px-4 font-medium">Forbruk</th>
                    <th className="py-2 px-4 font-medium text-right">Beløp</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} onClick={() => navigate(`/fakturering/${i.id}`)}
                      className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer">
                      <td className="py-2 px-4 font-mono text-xs text-emerald-400">{i.invoice_number}</td>
                      <td className="py-2 px-4 text-zinc-400">{fmtDate(i.issue_date)}</td>
                      <td className="py-2 px-4 text-zinc-400">{fmtDate(i.due_date)}</td>
                      <td className="py-2 px-4 tabular-nums text-zinc-300">{fmtKwh(i.consumption_kwh)}</td>
                      <td className="py-2 px-4 tabular-nums text-zinc-100 text-right">{fmtNOK(i.total_nok)}</td>
                      <td className="py-2 px-4"><StatusBadge value={i.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="saker" className="mt-4">
          <Card className="overflow-hidden">
            {cases.length === 0 ? <Empty title="Ingen saker" /> : (
              <div className="divide-y divide-zinc-900">
                {cases.map((c) => (
                  <div key={c.id} className="p-4 hover:bg-zinc-900/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-zinc-100">{c.subject}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{c.case_number} · {c.category} · {fmtDate(c.created_at)}</div>
                      </div>
                      <div className="flex gap-2">
                        <StatusBadge value={c.priority} />
                        <StatusBadge value={c.status} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
